import { useEffect, useState } from 'preact/hooks';
import { tg, initTelegram } from './lib/telegram';
import { authVerify, drainCheckInQueue, getUser } from './lib/api';
import { onOnline } from './lib/cache';
import { Inicio } from './screens/Inicio';
import { Eventos } from './screens/Eventos';
import { Ofertas } from './screens/Ofertas';
import { Pagos } from './screens/Pagos';
import { Disponibilidad } from './screens/Disponibilidad';
import { Perfil } from './screens/Perfil';
import { OpsEventos } from './screens/OpsEventos';
import { Tienda } from './screens/Tienda';
import { TabBar, type TabKey } from './components/TabBar';
import { Spinner } from './components/Spinner';

type AuthState = 'loading' | 'authed' | 'error';

// Deep-link intent parsed once at boot. Supports ?event=EV-XXXX&view=chat from
// the sophi-bot DM "Abrir chat" button (Sophi App v2 Piece 1.6).
function parseDeepLink(): { event: string; view: 'chat' } | null {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  const event = p.get('event');
  const view = p.get('view');
  if (event && view === 'chat') return { event, view: 'chat' };
  return null;
}

export function App() {
  const [state, setState] = useState<AuthState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState<{ event: string; view: 'chat' } | null>(parseDeepLink());
  const initialTab: TabKey = deepLink?.view === 'chat' ? 'eventos' : 'inicio';
  const [tab, setTab] = useState<TabKey>(initialTab);
  // Tienda is a full-screen route opened from Inicio/Perfil; remember where to
  // return so its back button lands on the originating tab.
  const [prevTab, setPrevTab] = useState<TabKey>('inicio');
  const openTienda = () => { setPrevTab(tab === 'tienda' ? 'inicio' : tab); setTab('tienda'); };

  useEffect(() => {
    initTelegram();
    const initData = tg?.initData || '';

    if (!initData && import.meta.env.DEV) {
      setState('authed');
      return;
    }

    if (!initData) {
      setError('Esta app debe abrirse desde Telegram.');
      setState('error');
      return;
    }

    authVerify(initData)
      .then(() => {
        setState('authed');
        // Drain any check-ins that were captured while offline. Safe to call
        // unconditionally — it's a no-op if the queue is empty or we're still
        // offline. Server-side idempotency (Upstash key per personalId+eventId)
        // protects against double-writes if a queued item happened to land at
        // the same time as a fresh tap.
        drainCheckInQueue().catch(() => { /* swallow — will retry on next online */ });
      })
      .catch((e: Error) => {
        setError(mapAuthError(e.message));
        setState('error');
      });
  }, []);

  // Drain offline queue whenever the browser transitions back online.
  // Persistent listener for the lifetime of the App component.
  useEffect(() => {
    return onOnline(() => {
      drainCheckInQueue().catch(() => { /* see above */ });
    });
  }, []);

  if (state === 'loading') return <Spinner label="Iniciando Sophi App..." />;

  if (state === 'error') {
    return (
      <div class="error-screen">
        <h2>No pudimos abrir tu cuenta</h2>
        <p>{error}</p>
        <p class="hint">Si el problema persiste, escríbele a Angie o a Sophi.</p>
      </div>
    );
  }

  return (
    <>
      {tab === 'inicio' && <Inicio onOpenTienda={openTienda} onGoToEventos={() => setTab('eventos')} />}
      {tab === 'eventos' && (
        <Eventos
          initialChatEventId={deepLink?.view === 'chat' ? deepLink.event : undefined}
          onConsumeInitialChat={() => setDeepLink(null)}
        />
      )}
      {tab === 'ofertas' && <Ofertas />}
      {tab === 'pagos' && <Pagos />}
      {tab === 'dispo' && <Disponibilidad />}
      {tab === 'ops' && <OpsEventos />}
      {tab === 'perfil' && <Perfil onOpenTienda={openTienda} />}
      {tab === 'tienda' && <Tienda onBack={() => setTab(prevTab)} />}
      <TabBar active={tab} onChange={setTab} showOps={!!getUser()?.isAdmin} />
    </>
  );
}

function mapAuthError(code: string): string {
  switch (code) {
    case 'staff_not_found':
      return 'Tu cuenta de Telegram aún no está vinculada a un perfil de staff. Angie puede ayudarte a vincularla.';
    case 'not_in_beta':
      return 'Sophi App está en beta. Angie te avisará cuando esté disponible para ti. Mientras tanto, puedes seguir usando los comandos de @Sofistics_bot.';
    case 'invalid_initData':
      return 'No pudimos validar tu sesión de Telegram. Cierra y vuelve a abrir la app.';
    case 'missing_initData':
      return 'Esta app debe abrirse desde Telegram.';
    default:
      return code || 'No pudimos verificar tu sesión.';
  }
}
